import axios from "axios";

// ── B2: Safety guard ──────────────────────────────────────────────────────────
// REACT_APP_BACKEND_URL is baked in at build time by CRA.
// If it is missing the baseURL becomes "undefined/api" and every call silently
// 404s. Fail fast in development; emit a loud error in production builds so the
// problem is visible in log aggregators rather than mysterious blank screens.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
if (!BACKEND_URL) {
  const msg =
    "[EDUHUB] REACT_APP_BACKEND_URL is not set. " +
    "All API calls will fail. Add REACT_APP_BACKEND_URL=https://api.eduhubcenter.com " +
    "to your .env file and rebuild the frontend.";
  if (process.env.NODE_ENV === "development") {
    throw new Error(msg);
  } else {
    // eslint-disable-next-line no-console
    console.error(msg);
  }
}

export const API = `${BACKEND_URL || ""}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// ── B1: Silent token refresh interceptor ─────────────────────────────────────
// When any API call returns 401, attempt one transparent refresh before giving
// up. This handles the case where the 24-hour access token has expired while
// the 14-day refresh token is still valid — the user stays logged in silently.
//
// Guards:
//  - config._retry  → prevents the retry itself from triggering another refresh
//  - /auth/refresh  → never retried (would cause an infinite loop on bad token)
//  - /auth/login    → skip (the user is deliberately unauthenticated here)

let _refreshInFlight = null; // single shared promise so concurrent 401s only fire one refresh

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const config = error.config;
    const status = error.response?.status;

    // Only intercept 401s on non-auth and non-retried requests
    if (
      status !== 401 ||
      config._retry ||
      config.url?.includes("/auth/refresh") ||
      config.url?.includes("/auth/login")
    ) {
      return Promise.reject(error);
    }

    config._retry = true;

    try {
      // If another request already kicked off a refresh, reuse its promise
      if (!_refreshInFlight) {
        _refreshInFlight = api
          .post("/auth/refresh")
          .finally(() => {
            _refreshInFlight = null;
          });
      }
      await _refreshInFlight;

      // The backend set fresh httpOnly cookies — retry the original request
      return api(config);
    } catch (refreshError) {
      // Refresh token is expired or invalid — user must log in again
      // Dispatch a custom DOM event so AuthContext can clear its state without
      // importing api (which would be circular).
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("eduhub:session-expired"));
      }
      return Promise.reject(refreshError);
    }
  },
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
